import React, { useState, useMemo } from 'react';
import type { Bot, GridBotStatus, LiveTrade, Instrument } from '../../../../types/trading';
import './BotList.css';

type BotTab = 'active' | 'history' | 'all';

interface BotListProps {
  bots: Bot[];
  gridBotStatuses: Record<string, GridBotStatus>;
  liveTrades: Record<string, LiveTrade[]>;
  instruments: Instrument[];
  selectedBotId: string | null;
  onBotSelect: (botId: string, symbol: string) => void;
  onPauseBot: (botId: string) => void;
  onResumeBot: (botId: string) => void;
  onStopBot: (botId: string) => void;
}

// Strategy display names
const STRATEGY_NAMES: Record<string, string> = {
  'grid_bot': 'Grid Bot',
  'dca_bot': 'DCA Bot',
  'scalping': 'Scalping',
};

export const BotList: React.FC<BotListProps> = ({
  bots,
  gridBotStatuses,
  liveTrades,
  instruments,
  selectedBotId,
  onBotSelect,
  onPauseBot,
  onResumeBot,
  onStopBot,
}) => {
  // Create a map for quick instrument lookup by FIGI
  const instrumentMap = useMemo(() => {
    const map = new Map<string, Instrument>();
    instruments.forEach(inst => map.set(inst.figi, inst));
    return map;
  }, [instruments]);

  // Helper to get display name for a symbol (FIGI)
  const getDisplayName = (figi: string) => {
    const inst = instrumentMap.get(figi);
    return inst ? inst.ticker : figi.slice(0, 10);
  };
  const [activeTab, setActiveTab] = useState<BotTab>('active');
  const [expandedBotId, setExpandedBotId] = useState<string | null>(null);

  // Filter bots based on tab
  const filteredBots = useMemo(() => {
    if (activeTab === 'active') return bots.filter(b => b.status === 'running' || b.status === 'paused');
    if (activeTab === 'history') return bots.filter(b => b.status === 'stopped');
    return bots;
  }, [bots, activeTab]);

  const formatMoney = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    return sign + value.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp * 1000);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: '2-digit' });
  };

  const handleBotClick = (bot: Bot) => {
    onBotSelect(bot.id, bot.symbol);
    setExpandedBotId(expandedBotId === bot.id ? null : bot.id);
  };

  const isEmpty = filteredBots.length === 0;

  // Count active bots
  const activeBotCount = bots.filter(b => b.status === 'running' || b.status === 'paused').length;
  const historyBotCount = bots.filter(b => b.status === 'stopped').length;

  return (
    <div className="bot-list">
      {/* Tabs */}
      <div className="bot-list-tabs">
        <div className="bot-list-tabs-left">
          <button
            className={`bot-list-tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTab('active')}
          >
            Активные боты {activeBotCount > 0 && <span className="tab-count">{activeBotCount}</span>}
          </button>
          <button
            className={`bot-list-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            История {historyBotCount > 0 && <span className="tab-count">{historyBotCount}</span>}
          </button>
          <button
            className={`bot-list-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            Все
          </button>
        </div>
      </div>

      {/* Content */}
      {isEmpty ? (
        <div className="bot-list-empty">
          <div className="bot-list-empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <circle cx="12" cy="12" r="10" />
              <path d="M8 12h8M12 8v8" />
            </svg>
          </div>
          <p className="bot-list-empty-text">
            {activeTab === 'active' ? 'Нет активных ботов' : activeTab === 'history' ? 'История пуста' : 'Нет ботов'}
          </p>
          <p className="bot-list-empty-hint">Настройте параметры и запустите бота</p>
        </div>
      ) : (
        <div className="bot-list-content">
          {filteredBots.map((bot) => {
            const status = gridBotStatuses[bot.id];
            const trades = liveTrades[bot.id] || [];
            const isExpanded = expandedBotId === bot.id;
            const isSelected = selectedBotId === bot.id;
            const pnl = status?.total_pnl ?? bot.pnl ?? 0;
            const winRate = status?.win_rate ?? 0;
            const totalTrades = status?.total_trades ?? trades.length;

            return (
              <div
                key={bot.id}
                className={`bot-card ${isSelected ? 'selected' : ''} ${isExpanded ? 'expanded' : ''}`}
              >
                {/* Bot Header Row */}
                <div className="bot-card-header" onClick={() => handleBotClick(bot)}>
                  {/* Expand Arrow */}
                  <div className={`bot-expand-icon ${isExpanded ? 'expanded' : ''}`}>
                    ▶
                  </div>

                  {/* Bot Info */}
                  <div className="bot-info">
                    <div className="bot-symbol">{getDisplayName(bot.symbol)}</div>
                    <div className="bot-strategy">{STRATEGY_NAMES[bot.strategy_id] || bot.strategy_id}</div>
                  </div>

                  {/* Status Badge */}
                  <div className={`bot-status-badge ${bot.status}`}>
                    <span className="status-dot"></span>
                    {bot.status === 'running' ? 'Работает' : bot.status === 'paused' ? 'Пауза' : 'Остановлен'}
                  </div>

                  {/* Stats */}
                  <div className="bot-stats">
                    <div className={`bot-stat pnl ${pnl >= 0 ? 'positive' : 'negative'}`}>
                      <span className="stat-value">{formatMoney(pnl)} ₽</span>
                      <span className="stat-label">Прибыль</span>
                    </div>
                    <div className="bot-stat">
                      <span className="stat-value">{winRate.toFixed(0)}%</span>
                      <span className="stat-label">Win Rate</span>
                    </div>
                    <div className="bot-stat">
                      <span className="stat-value">{totalTrades}</span>
                      <span className="stat-label">Сделок</span>
                    </div>
                  </div>

                  {/* Controls */}
                  <div className="bot-controls" onClick={(e) => e.stopPropagation()}>
                    {bot.status === 'running' ? (
                      <button
                        className="bot-control-btn pause"
                        onClick={() => onPauseBot(bot.id)}
                        title="Пауза"
                      >
                        ⏸
                      </button>
                    ) : bot.status === 'paused' ? (
                      <button
                        className="bot-control-btn resume"
                        onClick={() => onResumeBot(bot.id)}
                        title="Возобновить"
                      >
                        ▶
                      </button>
                    ) : null}
                    {bot.status !== 'stopped' && (
                      <button
                        className="bot-control-btn stop"
                        onClick={() => onStopBot(bot.id)}
                        title="Остановить"
                      >
                        ⏹
                      </button>
                    )}
                  </div>
                </div>

                {/* Expanded Content - Trades Table */}
                {isExpanded && (
                  <div className="bot-card-content">
                    {/* Bot Details */}
                    <div className="bot-details">
                      <div className="detail-item">
                        <span className="detail-label">Капитал:</span>
                        <span className="detail-value">{bot.capital.toLocaleString('ru-RU')} ₽</span>
                      </div>
                      <div className="detail-item">
                        <span className="detail-label">Запущен:</span>
                        <span className="detail-value">{formatDate(bot.started_at)}</span>
                      </div>
                      {status?.grid_range && (
                        <div className="detail-item">
                          <span className="detail-label">Сетка:</span>
                          <span className="detail-value">{status.grid_range}</span>
                        </div>
                      )}
                      {status?.active_orders !== undefined && (
                        <div className="detail-item">
                          <span className="detail-label">Активных ордеров:</span>
                          <span className="detail-value">{status.active_orders}</span>
                        </div>
                      )}
                    </div>

                    {/* Trades Table */}
                    <div className="bot-trades-table-container">
                      <table className="bot-trades-table">
                        <thead>
                          <tr>
                            <th>Время</th>
                            <th>Тип</th>
                            <th>Цена</th>
                            <th>Кол-во</th>
                            <th>P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trades.length > 0 ? (
                            trades.slice(0, 10).map((trade) => (
                              <tr key={trade.id}>
                                <td>{formatTime(trade.time)}</td>
                                <td>
                                  <span className={`trade-badge ${trade.type}`}>
                                    {trade.type === 'buy' ? 'Buy' : 'Sell'}
                                  </span>
                                </td>
                                <td>{trade.price.toFixed(2)}</td>
                                <td>{trade.quantity}</td>
                                <td className={trade.pnl != null && trade.pnl > 0 ? 'positive' : trade.pnl != null && trade.pnl < 0 ? 'negative' : ''}>
                                  {trade.pnl != null ? formatMoney(trade.pnl) : '-'}
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={5} className="empty-trades">Нет сделок</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      {trades.length > 10 && (
                        <div className="trades-more">
                          Показано 10 из {trades.length} сделок
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BotList;
